import Head from "next/head"
import { GlobalDataOptions } from "app/insights/GlobalDataOptions"
import { FC, PropsWithChildren, useEffect, useState } from "react"
import { Main } from "../components/layout/Main"
import { ApplicationContextProvider } from "../contexts/ApplicationContext"
import { BillingContextProvider } from "../contexts/BillingContext"
import dynamic from "next/dynamic"
import OnboardingProgressProvider from "app/onboardingV2/OnboardingContext"
import tw from "tailwind-styled-components"

import ReactDOM from "react-dom"
import { CommandBar } from "app/commandbar/CommandBar"
import { cn } from "@superwall/ui"
import { SidebarPlaceholder } from "./Sidebar"

// disable ssr
const Sidebar = dynamic(() => import("./Sidebar"), {
  ssr: false,
  loading: () => <SidebarPlaceholder />,
})

type PageWidth = "sm" | "md" | "base" | "xl" | "2xl"

export const SubtitleText = tw.p`
  max-w-4xl
  pt-0
  lg:pt-1
  opacity-60
`

export const SidebarLayoutAccessoryPortal: any = ({ children }) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  const portalRoot = document.getElementById("layout-accessory-portal")
  if (!portalRoot) {
    return null
  }

  return ReactDOM.createPortal(children, portalRoot)
}

const SidebarLayout: FC<
  PropsWithChildren<{
    fullWidth?: boolean
    title?: string
    pageWidth?: PageWidth
    accessoryItem?: React.ReactNode
    subtitle?: string | React.ReactNode
    paddingIndent?: string
  }>
> = ({ fullWidth, title, pageWidth = "xl", subtitle, accessoryItem, children, paddingIndent }) => {
  // remove bg class from the body element
  if (typeof window !== "undefined") {
    document.body.classList.remove("bg")
  }

  const pageWidthMap = {
    sm: "max-w-[768px]",
    md: "max-w-[1080px]",
    base: "max-w-[1200px]",
    xl: "max-w-[1440px]",
    "2xl": "max-w-[1600px]",
  }

  //  const pageWidthMap = {
  //    sm: "max-w-[768px]",
  //    md: "max-w-[1180px]",
  //    base: "max-w-[1300px]",
  //    xl: "max-w-[1540px]",
  //    "2xl": "max-w-[1700px]",
  //  }

  return (
    <div>
      <Head>
        <title>{title || "Dashboard"}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
        />
      </Head>
      <CommandBar />
      <ApplicationContextProvider>
        <BillingContextProvider>
          <OnboardingProgressProvider>
            <GlobalDataOptions>
              <div className="flex flex-col sm:flex-row min-h-[100vh]">
                <Sidebar></Sidebar>
                <div className="h-[64px] sm:w-[calc(100vw-72px)] lg:w-[calc(100vw-275px)]">
                  <Main
                    wrapperClassName={`${
                      fullWidth ? "mx-10" : pageWidthMap[pageWidth]
                    } grow mx-auto w-full`}
                    fullWidth={fullWidth}
                  >
                    <div className="2xl:h-10"></div>
                    {title && (
                      <div
                        className={cn(
                          "flex flex-row sm:gap-2 gap-4 pr-1 sm:pr-0 items-center justify-between flex-grow pb-6 lg:pb-10",
                          fullWidth && false ? "" : "pl-3 lg:pl-6",
                          paddingIndent
                        )}
                      >
                        <div className={` flex-grow`}>
                          <h1 className="text-2xl md:text-3xl pb-0">{title}</h1>
                          <span className="hidden sm:inline-block">
                            {subtitle && typeof subtitle === "string" ? (
                              <SubtitleText>{subtitle}</SubtitleText>
                            ) : (
                              subtitle
                            )}
                          </span>
                        </div>
                        <div id="layout-accessory-portal">{accessoryItem && accessoryItem}</div>
                      </div>
                    )}

                    {children}

                    <div className="min-h-[96px]"></div>
                  </Main>
                </div>
              </div>
            </GlobalDataOptions>
          </OnboardingProgressProvider>
        </BillingContextProvider>
      </ApplicationContextProvider>
    </div>
  )
}

export default SidebarLayout
// SidebarSpacer
// h-[64px]
// min-h-[64px]
// sm:w-[72px]
// sm:min-w-[72px]
// lg:w-[275px]
// lg:min-w-[275px]
// lg:relative
